// Resolve o prazo do agrupamento de uma UC num mês (#5787) — fonte única para quem precisa do
// limite de emissão fora da tela de Agrupamentos.
//
// POR QUE EXISTE: a régua depende de três coisas que moram no banco — o cadastro de feriados, a
// régua própria da Secretaria e as datas ajustadas daquele mês. A tela de acompanhamento já lia
// as três; o modal de emissão lia nenhuma e calculava com o padrão em código. Resultado: a mesma
// competência aparecia "no prazo" numa tela e "atrasada" na outra. Quem precisar do prazo (a
// emissão, e depois a NF/malote e a auditoria) chama daqui, em vez de remontar na mão.

import { supabase } from './supabase.ts';
import { montarRegua, type ReguaPrazos } from './reguaPrazosAgrupamento.ts';

const norm = (v: string | number | null | undefined) =>
  String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

export interface PrazoDaUc {
  agrupamentoId: number;
  agrupamentoNome: string;
  regua: ReguaPrazos;
}

/**
 * Devolve o prazo do agrupamento ATIVO da UC na competência, ou null quando a UC não está em
 * agrupamento nenhum (aí vale a régua individual de janelaFaturamento.ts).
 *
 * Erro de consulta vira `null`: o lado seguro é cair na régua individual, que pede justificativa
 * a mais, nunca a menos. Quem chama decide se avisa o usuário.
 */
export async function prazoAgrupamentoDaUc(
  numeroUc: string | null | undefined,
  competenciaIso: string | null | undefined,
  hojeIso: string,
): Promise<PrazoDaUc | null> {
  const uc = norm(numeroUc);
  const competencia = String(competenciaIso ?? '').slice(0, 10);
  if (!uc || !/^\d{4}-\d{2}-\d{2}$/.test(competencia)) return null;

  const { data: vinculos, error } = await supabase
    .from('vw_agrupamento_faturamento_uc')
    .select('agrupamento_id, agrupamento_nome, agrupamento_ativo, numero_uc, dia_vencimento')
    .eq('agrupamento_ativo', true)
    .ilike('numero_uc', `%${uc}%`);
  if (error) throw error;

  const linha = (vinculos ?? []).find((l: any) => norm(l.numero_uc) === uc);
  if (!linha) return null;

  const [{ data: grupo }, { data: fechamento }, { data: feriados }] = await Promise.all([
    supabase.from('agrupamentos_faturamento')
      .select('dia_vencimento, dia_limite_emissao, dia_auditoria_util, dia_malote_util, dias_pagamento_edital')
      .eq('id', (linha as any).agrupamento_id).maybeSingle(),
    supabase.from('agrupamento_fechamentos')
      .select('limite_emissao_ajustado, auditoria_ajustada, vencimento_ajustado, malote_ajustado, emitido_em')
      .eq('agrupamento_id', (linha as any).agrupamento_id).eq('competencia', competencia).maybeSingle(),
    // Dois anos bastam: a régua olha a competência e o mês seguinte.
    supabase.from('feriados').select('data, tipo')
      .gte('data', `${Number(competencia.slice(0, 4))}-01-01`)
      .lte('data', `${Number(competencia.slice(0, 4)) + 1}-12-31`),
  ]);

  const regua = montarRegua({
    competencia,
    diaVencimento: Number((grupo as any)?.dia_vencimento ?? (linha as any).dia_vencimento) || 15,
    hojeIso,
    emitidoEm: (fechamento as any)?.emitido_em ?? null,
    feriados: {
      incluir: (feriados ?? []).filter((f: any) => f.tipo === 'inclui').map((f: any) => String(f.data).slice(0, 10)),
      excluir: (feriados ?? []).filter((f: any) => f.tipo === 'exclui').map((f: any) => String(f.data).slice(0, 10)),
    },
    regra: {
      diaLimiteEmissao: (grupo as any)?.dia_limite_emissao ?? null,
      diaAuditoriaUtil: (grupo as any)?.dia_auditoria_util ?? null,
      diaMaloteUtil: (grupo as any)?.dia_malote_util ?? null,
      diasPagamentoEdital: (grupo as any)?.dias_pagamento_edital ?? null,
    },
    ajustes: {
      limiteEmissao: (fechamento as any)?.limite_emissao_ajustado ?? null,
      auditoria: (fechamento as any)?.auditoria_ajustada ?? null,
      vencimento: (fechamento as any)?.vencimento_ajustado ?? null,
      malote: (fechamento as any)?.malote_ajustado ?? null,
    },
  });

  return {
    agrupamentoId: (linha as any).agrupamento_id,
    agrupamentoNome: (linha as any).agrupamento_nome,
    regua,
  };
}
